'use client';

import React from 'react';
import { Zap } from 'lucide-react';
import type { PostCardProps } from './post-card';
import { stripMarkdown } from '@/lib/community/utils';
import { SmartCoverImage } from './smart-cover-image';
import { PostCardStats } from './post-card-stats';

export const PostCardSkill = React.memo(function PostCardSkill({ post, onClick, onTagClick, onLike, onFavorite }: PostCardProps) {
	return (
		<div
			className="liquid-glass-card rounded-md overflow-hidden cursor-pointer"
			onClick={(e) => {
				const card = e.currentTarget;
				const cover = card.querySelector('[data-cover]');
				onClick?.(post, card.getBoundingClientRect(), cover?.getBoundingClientRect() ?? undefined);
			}}
		>
			{post.coverImage && (
				<div data-cover>
					<SmartCoverImage alt={post.title} src={post.coverImage} />
				</div>
			)}

			<div className="p-3 flex flex-col gap-2">
				<h3 className="font-medium text-sm leading-snug truncate">{post.title}</h3>

				<p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">{stripMarkdown(post.description)}</p>

				{post.skillDetail && (
					<div className="flex items-start gap-1.5 rounded-md border border-blue-500/25 bg-blue-500/10 px-2 py-1.5 w-fit">
						<Zap className="w-3 h-3 text-blue-500 shrink-0 mt-0.5" />
						<div className="flex-1 min-w-0">
							<span className="text-[10px] font-medium text-foreground truncate block">{post.skillDetail.name}</span>
							<span className="text-[9px] text-muted-foreground truncate block">
								{post.skillDetail.privacyLabel} · {post.skillDetail.source}
							</span>
						</div>
					</div>
				)}

				{post.tags.length > 0 && (
					<div className="flex flex-wrap gap-1">
						{post.tags.slice(0, 3).map((tag) => (
							<button
								className="inline-block rounded-full bg-secondary/70 text-foreground/70 px-2 py-0.5 text-[10px] hover:text-foreground hover:bg-secondary transition-colors cursor-pointer"
								key={tag}
								onClick={(e) => {
									e.stopPropagation();
									onTagClick?.(tag);
								}}
								type="button"
							>
								#{tag}
							</button>
						))}
					</div>
				)}

				<PostCardStats onFavorite={onFavorite} onLike={onLike} post={post} />
			</div>
		</div>
	);
});
